import TimesIcon from '@mui/icons-material/Cancel';
import UploadIcon from '@mui/icons-material/CloudUploadOutlined';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import Card from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import cx from 'classnames';
import Avatar from 'components/Avatar';
import FileAttachments from 'components/FileAttachment';
import IconButton from 'components/IconButton/IconButton';
import FormLabel from 'components/form/FormLabel';
import { DragDropIcon, FileIcon } from 'components/icons';
import React, { useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { isImage } from 'utils';

const thumbInner = {
  // display: 'flex',
  minWidth: 0,
  overflow: 'hidden',
  border: '2px solid #eaeaea',
  padding: '4px',
};

const StyledRoot = styled(Box)`
  background-color: ${({ theme }) => theme.palette.input.background};
  border-radius: 8px;
  padding: 32px 16px;
  text-align: center;
  border: 1px dashed
  ${({ theme, error }) =>
    error ? theme.palette.error.main : theme.palette.text.subtle};

`;

function Dropzone({
  accept,
  placeholder,
  onChange,
  showPreview = true,
  className,
  dropzoneProps,
  hasError,
  bgColor,
  margin = 'normal',
  label,
  helpText = {},
  multiple = false,
  value,
}) {
  const [files, setFiles] = useState([]);
  const [t] = useTranslation();
  const { getRootProps, getInputProps } = useDropzone({
    accept,
    multiple,
    onDrop: (acceptedFiles) => {
      const result = acceptedFiles.map((file) =>
        Object.assign(file, {
          preview: URL.createObjectURL(file),
        }),
      );
      const newFiles = multiple ? [...files, ...result] : result;
      setFiles(newFiles);
      onChange && onChange(newFiles);
    },
    onDropRejected: () => {
      if (dropzoneProps.maxSize) {
        const kb = dropzoneProps.maxSize / 1000;
        toast.error(`File Size Exceeds Required Limit of: ${kb} KB`);
      } else {
        toast.error('File type not allowed');
      }
    },
    ...dropzoneProps,
  });

  useEffect(() => {
    const urlToFile = async (url) => {
      try {
        const response = await fetch(url);
        const blob = await response.blob();
        const filename = url.substring(url.lastIndexOf('/') + 1);
        const file = new File([blob], filename, { type: blob.type });
        file.preview = URL.createObjectURL(file);
        return file;
      } catch (error) {
        console.error('Error fetching image:', error);
        return null;
      }
    };

    const processSingleValue = async (item) => {
      if (typeof item === 'string') {
        return await urlToFile(item);
      }
      return item;
    };

    const processValue = async () => {
      if (!value) {
        setFiles([]);
        return;
      }
      try {
        let newFiles = [];

        if (typeof value === 'string') {
          const file = await urlToFile(value);
          newFiles = file ? [file] : [];
        } else if (Array.isArray(value)) {
          const processedFiles = await Promise.all(
            value.map(processSingleValue),
          );
          newFiles = processedFiles.filter((file) => file !== null);
        } else {
          newFiles = [value];
        }

        setFiles(newFiles);
      } catch (error) {
        console.error('Error processing files:', error);
        setFiles([]);
      }
    };

    processValue();
  }, [value]);

  const removeFile = (e, file) => {
    e.stopPropagation();
    if (file.preview) {
      URL.revokeObjectURL(file.preview);
    }
    const filtered = files.filter((f) => f !== file);
    setFiles(filtered);
    onChange && onChange(filtered);
  };

  const thumbs = files.map((file, i) => (
    <div style={thumbInner} key={i}>
      {isImage(file.name) ? (
        <img src={file.preview} />
      ) : (
        <Typography component="span" textOverflow="ellipsis">
          {file.name}
        </Typography>
      )}
      <IconButton
        title="Remove"
        color="error"
        icon={<TimesIcon />}
        className="close"
        onClick={(e) => removeFile(e, file)}
        size="small"
      />
    </div>
  ));

  // useEffect(
  //   () => () => {
  //     // Make sure to revoke the data uris to avoid memory leaks
  //     files.forEach((file) => URL.revokeObjectURL(file.preview));
  //   },
  //   [files],
  // );

  let showDropzone = true;
  if (!multiple && files.length > 0) {
    showDropzone = false;
  }
  return (
    <FormControl
      fullWidth
      margin={margin}
      error={hasError}
      className={className}
    >
      {label && (
        <FormLabel sx={{ mb: 0.5, display: 'block' }}>{label}</FormLabel>
      )}
      {showDropzone && (
        <StyledRoot
          bgColor={bgColor}
          {...getRootProps({ className: 'dropzone', error: hasError })}
        >
          <div>
            <input {...getInputProps()} />
            <Box>
              {placeholder || (
                <Stack alignItems="center" spacing={1}>
                  <Avatar
                    size="xlarge"
                    color="secondary"
                    fillMode="dark"
                    sx={{
                      bgcolor: 'background.paper3',
                      border: 1,
                      borderColor: 'dividerVariant',
                    }}
                  >
                    <DragDropIcon />
                  </Avatar>
                  <Typography variant="caption" color="textSecondary">
                    {t('click-or-drag', 'Click here or drag and drop')}
                  </Typography>
                </Stack>
              )}
            </Box>
          </div>
          {hasError && <FormHelperText error>{helpText}</FormHelperText>}
        </StyledRoot>
      )}
      {showPreview && files.length ? (
        <FileAttachments files={files} onRemove={removeFile} />
      ) : null}
    </FormControl>
  );
}

Dropzone.defaultProps = {
  accept: {
    'image/*': ['.jpg', '.jpeg', '.png'],
  },
  dropzoneProps: {},
};

export default React.forwardRef((props, ref) => (
  <Dropzone {...props} forwardedRef={ref} />
));
